// config.ts
import { getEnvironment } from "./environment-checks.js";
import { baseConfigName } from "./baseConfigName.js";
var UndefinedError = class extends Error {
  constructor(key) {
    super(`Env var ${key} is required but not defined.`);
  }
};
var ClientAccessError = class extends Error {
  constructor(key) {
    super(`Config value "${String(key)}" is only accessible on the server.`);
  }
};
var logLevel = ["trace", "debug", "info", "warn", "error"];
var Config = class {
  constructor(configKey, config) {
    this.configKey = configKey;
    this._logLevel = 2;
    this._definitions = { ...config };
    const env = getEnvironment();
    this._parsed = env === "node" ? this._loadServer() : this._loadClient();
  }
  /**
   * Exports all parsed values which are allowed to be sent to clients as a plain object.
   */
  exportClientEnv() {
    const env = {};
    for (const [key, def] of Object.entries(this._definitions)) {
      if (def.client)
        env[def.name] = this._parsed[key];
    }
    delete env.ENVIRONMENT;
    return env;
  }
  /**
   * Creates the js script neccessary for ssr, so the client config can be initialized correctly.
   *
   * This can be used in ssr templates like so:
   *
   * ```ts
   * html`
   *   <script> ${configInstance.exportClientScript()} </script>
   * `
   * ```
   */
  exportClientScript() {
    return `window["adornisConfig_${this.configKey}"] = ${JSON.stringify(this.exportClientEnv())};`;
  }
  exportLegacyCompatClientScript() {
    return `window.A_CONFIG = { ROOT_URL: '${this._parsed.ROOT_URL}', DEVELOPMENT: '${this._parsed.DEPLOYMENT === "development"}', CLIENT: 'true' };`;
  }
  /**
   * Access values inside this config instance. All possible keys are available via intellisense.
   * If not, they are the keys of the ConfigDefinition object supplied to the config constructor.
   *
   * This function throws an ClientAccessError if values not marked as client accessible are requested on non node environments.
   */
  get(key) {
    if (getEnvironment() !== "node" && !this._definitions[key]?.client)
      throw new ClientAccessError(key);
    return this._parsed[key];
  }
  // * we cannot use logger from @adornis/base here since @adornis/base depends on @adornis/config, so we have to provide our own crude logger
  _logIfLevel(level, ...args) {
    const targetLevelIndex = logLevel.indexOf(level);
    if (targetLevelIndex >= this._logLevel) {
      console[level === "info" ? "log" : level === "trace" ? "debug" : level](...args);
    }
  }
  _loadClient() {
    this._logLevel = logLevel.indexOf(window[`adornisConfig_${baseConfigName}`]?.LOG_LEVEL ?? "info");
    const configKey = `adornisConfig_${this.configKey}`;
    const configObj = window[configKey];
    if (!configObj)
      throw new Error(
        `Config with key ${configKey} was not transferred to the client. Please make sure to include the config in the indexRenderer config array or make sure it is not imported on the client.`
      );
    const results = {};
    for (const [key, def] of Object.entries(this._definitions)) {
      if (!def.client)
        continue;
      const value = configObj[def.name];
      results[key] = this._parseValue(value, key, def);
    }
    this._logIfLevel("trace", Object.keys(results), "loaded config");
    return results;
  }
  _loadServer() {
    this._logLevel = logLevel.indexOf(process.env.LOG_LEVEL ?? "info");
    const results = {};
    for (const [key, def] of Object.entries(this._definitions)) {
      const value = def.env ? process.env[def.name] : void 0;
      results[key] = this._parseValue(value, key, def);
    }
    this._logIfLevel("trace", Object.keys(results), "loaded config");
    return results;
  }
  _parseValue(value, key, def) {
    try {
      this._logIfLevel("trace", `loading env var ${def.name} => ${key}`);
      if (!def.env)
        return def.value;
      if (def.required) {
        if (!value)
          throw new UndefinedError(def.name);
        const parsed = def.parse?.(value);
        return parsed === void 0 ? value : parsed;
      }
      if (value !== void 0) {
        const parsed = def.parse?.(value);
        return parsed !== void 0 ? parsed : value;
      }
      return def.defaultVal;
    } catch (error) {
      this._logIfLevel("warn", `Error while parsing config key ${key} from definition ${def.name}`, error);
      if (!def.env || def.required) {
        throw error;
      }
      this._logIfLevel("warn", `Using defualt value for config key ${key} from definition ${def.name}`);
      return def.defaultVal;
    }
  }
};
export {
  ClientAccessError,
  Config,
  UndefinedError
};
